import { BaseModal, BaseModalProps, Button, Container } from "@animech/ui";
import { useTheme } from "@emotion/react";
import { FunctionComponent, ReactNode, useState } from "react";
import { useUiData } from "src/ui";

const errorMessages = {
	launchFailed: (
		<>
			<p>
				Failed to launch AR. If you&apos;re on a device running Android, please
				make sure that:
			</p>
			<ul>
				<li>
					Your device is included in{" "}
					<a
						href="https://developers.google.com/ar/devices#google_play_devices"
						target="_blank"
						rel="noreferrer"
					>
						this list
					</a>{" "}
					of AR supported devices
				</li>
				<li>
					<a
						href="https://play.google.com/store/apps/details?id=com.google.ar.core"
						target="_blank"
						rel="noreferrer"
					>
						Google Play Services for AR
					</a>{" "}
					is installed and up to date
				</li>
			</ul>
		</>
	),
};

export type ARModalProps = BaseModalProps & {
	errorMessage?: keyof typeof errorMessages;
	customErrorMessage?: ReactNode;
	onLaunch?: () => void;
};

export const ARModal: FunctionComponent<ARModalProps> = (props) => {
	const {
		errorMessage: errorMessageId,
		customErrorMessage,
		onLaunch: onLaunchFromProps,
		onShouldClose,
		...baseProps
	}: ARModalProps = props;

	const { maxWidthSmall } = useTheme().app.modals;

	const { localizer } = useUiData();

	const [errorMessage, setErrorMessage] = useState<ReactNode>(
		customErrorMessage ??
			(errorMessageId ? errorMessages[errorMessageId] : undefined),
	);

	const onLaunch = onLaunchFromProps
		? () => {
				try {
					onLaunchFromProps();
					onShouldClose?.();
				} catch (e: unknown) {
					setErrorMessage(e instanceof Error ? e.message : String(e));
				}
			}
		: undefined;

	return (
		<BaseModal
			{...baseProps}
			onShouldClose={onShouldClose}
			maxWidth={maxWidthSmall}
		>
			<Container padding="large">
				{errorMessage && (
					<>
						<h3>{localizer.localizeTerm("arModalErrorTitle")}</h3>
						{typeof errorMessage === "string" ? (
							<p>{errorMessage}</p>
						) : (
							errorMessage
						)}
					</>
				)}
				{onLaunch && (
					<>
						<h3>{localizer.localizeTerm("arModalTitle")}</h3>
						<p>{localizer.localizeTerm("arModalBody")}</p>
						<Button type="block" icon="ar-2" onClick={onLaunch}>
							{localizer.localizeTerm("arModalLaunch")}
						</Button>
					</>
				)}
			</Container>
		</BaseModal>
	);
};
